import { Card, message, Space, Switch, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PagingParams } from 'Modules'
import { useCallback, useEffect, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import ProtectedButton from 'component/button/protected-button'
import { BANNER_TYPES } from 'constants/banner'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { FEATURE } from 'constants/feature'
import useSearchParams from 'hooks/use-search-params'
import { logError } from 'utils/logger'

import BannerModal from './components/banner-modal'

import './style.scss'

export interface BannerType {
  _id: string
  name: string
  image: string
  link: string
  type: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

const Banner = () => {
  const { searchParams, initPagination, onChangePagination } = useSearchParams<PagingParams>()

  const [filteredData, setFilteredData] = useState<BannerType[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingBannerId, setEditingBannerId] = useState<string | null>(null)

  const additionalItems = [BREADCRUMBS.BANNER]

  const getData = useCallback(async () => {
    try {
      setLoading(true)
      const res = await AppAdmin.getListBanner(searchParams.limit, searchParams.page)
      if (res?.data.success) {
        const { results, totalResults } = res.data?.data || {}
        setTotal(totalResults || 0)
        setFilteredData(results || [])
      }
    } catch (error) {
      setTotal(0)
      setFilteredData([])
      logError('Error: ', error)
    } finally {
      setLoading(false)
    }
  }, [searchParams.limit, searchParams.page])

  const handleOpenCreateModal = () => {
    setEditingBannerId(null)
    setIsModalOpen(true)
  }

  const handleOpenEditModal = (record: BannerType) => {
    setEditingBannerId(record._id)
    setIsModalOpen(true)
  }

  const handleModalCancel = () => {
    setIsModalOpen(false)
    setEditingBannerId(null)
  }

  const handleModalSuccess = () => {
    handleModalCancel()
    getData()
  }

  const handleToggleActive = async (record: BannerType, checked: boolean) => {
    try {
      const res = await AppAdmin.updateBanner(record._id, {
        name: record.name,
        image: record.image,
        link: record.link,
        type: record.type,
        isActive: checked,
      })
      if (res?.data.success) {
        message.success(`Banner ${checked ? 'activated' : 'deactivated'} successfully`)
        getData()
      } else {
        message.error(res?.data.message || 'Failed to update banner status')
      }
    } catch (error) {
      logError('Error: ', error)
      message.error('An error occurred')
    }
  }

  useEffect(() => {
    getData()
  }, [getData])

  const columns: ColumnsType<BannerType> = [
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      render: (image) => (
        <div className="banner-image">
          <img src={image} alt="banner" />
        </div>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => {
        const typeObj = BANNER_TYPES.find((t) => t.value === type)
        return typeObj ? typeObj.label : type
      },
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive, record) => (
        <Switch checked={isActive} onChange={(checked) => handleToggleActive(record, checked)} />
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <ProtectedButton
            className="button-edit"
            onClick={() => handleOpenEditModal(record)}
            feature={FEATURE.BANNER} requireCRUD
          >
            Edit
          </ProtectedButton>
        </Space>
      ),
    },
  ]

  return (
    <div className="main-section">
      <Breadcrumb additionalItems={additionalItems} />
      <div className="main-title">Banner Management</div>
      <div className="table-row">
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <ProtectedButton
            type="primary"
            onClick={handleOpenCreateModal}
            style={{ marginBottom: '20px' }}
            feature={FEATURE.BANNER} requireCRUD
          >
            Create
          </ProtectedButton>
        </div>
        <Card>
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey={(record) => record._id}
            loading={loading}
            pagination={initPagination(total)}
            onChange={onChangePagination}
            scroll={{ x: 1000 }}
          />
        </Card>
      </div>

      <BannerModal
        open={isModalOpen}
        id={editingBannerId}
        onCancel={handleModalCancel}
        onSuccess={handleModalSuccess}
      />
    </div>
  )
}

export default Banner
